// Libraries
import clsx from "clsx";
import { Button, ClickAwayListener, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";

// Types
import { TDisplayReason } from "../../../types/dealerTrades";

// Icons
import { ReactComponent as ThumbDownIcon } from "@/Washouts/assets/icons/ThumbDownIcon.svg";
import { ReactComponent as ThumbUpIcon } from "@/Washouts/assets/icons/ThumbUpIcon.svg";

// Components
import ReasonCard from "./ReasonCard";

type TChoiceComponentProps = {
	icon: JSX.Element;
	isOpen: boolean;
	displayReason: TDisplayReason;
	textApprove: string;
	textCancel: string;
	amountSelected: number;
	disabled: boolean;
	hidden: boolean;
	disabledButtons?: {
		approve: boolean;
		cancel: boolean;
	};
	// Callbacks
	onClickWayHandle: () => void;
	onApproveHandle: () => void;
	onCancelHandle: (reason: string) => void;
	onDisplayReason: () => void;
	setOpenOption: () => void;
};

export default function ChoiceComponent(props: Readonly<TChoiceComponentProps>) {
	const {
		icon,
		isOpen,
		displayReason,
		textApprove,
		textCancel,
		amountSelected,
		disabled,
		hidden,
		disabledButtons,
		// Callbacks
		onClickWayHandle,
		onApproveHandle,
		onCancelHandle,
		onDisplayReason,
		setOpenOption,
	} = props;

	if (hidden) {
		return null;
	}

	return (
		<>
			{isOpen && (
				<ClickAwayListener onClickAway={() => onClickWayHandle()}>
					<Stack direction={"row"} spacing={2}>
						<Tooltip
							title={"Trade has already been approved, can only be rejected"}
							arrow
							disableHoverListener={!disabledButtons?.approve}
						>
							<Paper
								aria-disabled={disabledButtons?.approve}
								elevation={0}
								className={clsx({
									"h-[30px] !shadow-none !rounded-md": true,
									"!bg-primary hover:!shadow-primary-shadow-50": !disabledButtons?.approve,
									"!bg-disabled": disabledButtons?.approve,
								})}
							>
								<Button
									variant="contained"
									aria-controls="select-plan-dialog-assign-btn-menu"
									className={clsx({
										"h-[30px] !shadow-none !rounded-md": true,
										"!bg-primary hover:!shadow-primary-shadow-50": !disabledButtons?.approve,
										"!bg-disabled": disabledButtons?.approve,
									})}
									endIcon={<ThumbUpIcon />}
									onClick={onApproveHandle}
									disabled={disabledButtons?.approve}
								>
									<Typography className="change-request-btn-text">{textApprove}</Typography>
								</Button>
							</Paper>
						</Tooltip>

						<Tooltip title={"GPS device transfer is in progress"} arrow disableHoverListener={!disabledButtons?.cancel}>
							<Paper
								aria-disabled={disabledButtons?.cancel}
								elevation={0}
								className={clsx({
									"h-[30px] !shadow-none !rounded-md !mr-[25px]": true,
									"!bg-gray-blue hover:!shadow-gray-blue-50": !disabledButtons?.cancel,
									"!bg-disabled": disabledButtons?.cancel,
								})}
							>
								<Button
									variant="contained"
									aria-controls="select-plan-dialog-assign-btn-menu"
									className={clsx({
										"h-[30px] !rounded-[6px] hover:!shadow-gray-blue !shadow-none": true,
										"!bg-gray-blue": !disabledButtons?.cancel,
										"!bg-disabled": disabledButtons?.cancel,
									})}
									endIcon={<ThumbDownIcon />}
									onClick={onDisplayReason}
									disabled={disabledButtons?.cancel}
								>
									<Typography className="change-request-btn-text">{textCancel}</Typography>
								</Button>
							</Paper>
						</Tooltip>
						{displayReason.show && (
							<ReasonCard
								amountSelected={amountSelected}
								className="top-[40px] left-[17px] "
								buttonText={textCancel}
								onClick={onCancelHandle}
								onClickAway={() => onClickWayHandle()}
							/>
						)}
					</Stack>
				</ClickAwayListener>
			)}

			<IconButton size="small" className="w-[35px]" onClick={() => setOpenOption()} disabled={disabled}>
				{icon}
			</IconButton>
		</>
	);
}
